import type { PineBinaryOp, PineExpr, PineProgram, PineStmt } from "./ast";

const INDENT = "    ";

// Pine-equivalent operator precedence. Higher binds tighter. Mirrors the
// usual C-family ordering so manually-built expressions render as a user
// would expect when read aloud. The renderer auto-parenthesizes whenever
// a child operator is below this table's precedence relative to its
// parent context. See {@link renderBinary}.
const BINARY_PRECEDENCE: Record<PineBinaryOp, number> = {
  or: 1,
  and: 2,
  "==": 3,
  "!=": 3,
  "<": 4,
  ">": 4,
  "<=": 4,
  ">=": 4,
  "+": 5,
  "-": 5,
  "*": 6,
  "/": 6,
};

// Ternary lives below `or` in precedence - if any child is a ternary it
// always needs parens inside any binary or unary context.
const TERNARY_PRECEDENCE = 0;

// Unary ops bind tighter than any binary operator we model.
const UNARY_PRECEDENCE = 7;

// Atoms (literals, idents, calls, members, paren) cannot be ambiguous.
const ATOM_PRECEDENCE = Infinity;

function expressionPrecedence(node: PineExpr): number {
  switch (node.kind) {
    case "binary":
      return BINARY_PRECEDENCE[node.op];
    case "unary":
      return UNARY_PRECEDENCE;
    case "ternary":
      return TERNARY_PRECEDENCE;
    default:
      return ATOM_PRECEDENCE;
  }
}

function renderString(value: string): string {
  // Mirror JSON's double-quote escaping. Strategy names are sanitized upstream
  // so we never see embedded quotes here in practice, but this keeps the
  // renderer total.
  return JSON.stringify(value);
}

function renderNumber(value: number): string {
  if (!Number.isFinite(value)) return "na";
  return String(value);
}

function renderInContext(node: PineExpr, parentPrec: number, side: "left" | "right" | "single"): string {
  const childPrec = expressionPrecedence(node);
  // For left-associative binary operators, the left child can keep
  // equal precedence without parens; the right child needs parens at
  // equal precedence so `a - (b - c)` is distinguishable from
  // `(a - b) - c`. Unary/ternary children (single context) always paren
  // when below parent.
  const needsParens =
    side === "right" ? childPrec <= parentPrec : childPrec < parentPrec;
  const out = renderExpr(node);
  return needsParens ? `(${out})` : out;
}

function renderExpr(node: PineExpr): string {
  switch (node.kind) {
    case "lit-string":
      return renderString(node.value);
    case "lit-number":
      return renderNumber(node.value);
    case "lit-bool":
      return node.value ? "true" : "false";
    case "lit-na":
      return "na";
    case "ident":
      return node.name;
    case "member":
      return `${renderExpr(node.object)}.${node.property}`;
    case "call":
      return `${node.callee}(${renderArgs(node.args, node.namedArgs)})`;
    case "binary": {
      const prec = BINARY_PRECEDENCE[node.op];
      const left = renderInContext(node.left, prec, "left");
      const right = renderInContext(node.right, prec, "right");
      return `${left} ${node.op} ${right}`;
    }
    case "unary": {
      const operand = renderInContext(node.operand, UNARY_PRECEDENCE, "single");
      return node.op === "not" ? `not ${operand}` : `-${operand}`;
    }
    case "ternary": {
      // Ternary children render in atom context - parens around any
      // non-atom keeps `a > b ? x + y : z` parsing as intended.
      const cond = renderInContext(node.cond, TERNARY_PRECEDENCE, "single");
      const then = renderInContext(node.then, TERNARY_PRECEDENCE, "single");
      const otherwise = renderInContext(
        node.otherwise,
        TERNARY_PRECEDENCE,
        "single",
      );
      return `${cond} ? ${then} : ${otherwise}`;
    }
    case "paren":
      return `(${renderExpr(node.inner)})`;
  }
}

function renderArgs(
  args: PineExpr[],
  namedArgs?: Array<[string, PineExpr]>,
): string {
  const positional = args.map(renderExpr);
  const named = (namedArgs ?? []).map(
    ([key, value]) => `${key}=${renderExpr(value)}`,
  );
  return [...positional, ...named].join(", ");
}

function renderInputOpts(opts: {
  minval?: number;
  maxval?: number;
  step?: number;
}): string {
  const parts: string[] = [];
  if (opts.minval !== undefined) parts.push(`minval=${renderNumber(opts.minval)}`);
  if (opts.maxval !== undefined) parts.push(`maxval=${renderNumber(opts.maxval)}`);
  if (opts.step !== undefined) parts.push(`step=${renderNumber(opts.step)}`);
  return parts.length > 0 ? `, ${parts.join(", ")}` : "";
}

function renderTarget(target: string | string[]): string {
  return typeof target === "string" ? target : `[${target.join(", ")}]`;
}

function renderStmt(node: PineStmt, indent: number): string[] {
  const pad = INDENT.repeat(indent);
  switch (node.kind) {
    case "comment":
      return [`${pad}// ${node.text}`];
    case "blank":
      return [""];
    case "version":
      return [`//@version=${node.version}`];
    case "strategy-decl": {
      const args = renderArgs([{ kind: "lit-string", value: node.name }], node.namedArgs);
      return [`${pad}strategy(${args})`];
    }
    case "input-int": {
      const opts = renderInputOpts({
        minval: node.minval,
        maxval: node.maxval,
        step: node.step,
      });
      return [
        `${pad}${node.name} = input.int(${renderNumber(node.defaultValue)}, ${renderString(node.title)}${opts})`,
      ];
    }
    case "input-float": {
      const opts = renderInputOpts({
        minval: node.minval,
        maxval: node.maxval,
        step: node.step,
      });
      return [
        `${pad}${node.name} = input.float(${renderNumber(node.defaultValue)}, ${renderString(node.title)}${opts})`,
      ];
    }
    case "input-bool":
      return [
        `${pad}${node.name} = input.bool(${node.defaultValue ? "true" : "false"}, ${renderString(node.title)})`,
      ];
    case "assign": {
      const head = node.declaration ? `${node.declaration} ` : "";
      return [
        `${pad}${head}${renderTarget(node.target)} = ${renderExpr(node.value)}`,
      ];
    }
    case "reassign":
      return [`${pad}${node.target} := ${renderExpr(node.value)}`];
    case "if": {
      const header = `${pad}if ${renderExpr(node.condition)}`;
      const body = node.body.flatMap((child) => renderStmt(child, indent + 1));
      return [header, ...body];
    }
    case "call-stmt":
      return [
        `${pad}${node.callee}(${renderArgs(node.args, node.namedArgs)})`,
      ];
  }
}

export function renderProgram(program: PineProgram): string {
  return program.statements
    .flatMap((s) => renderStmt(s, 0))
    .join("\n");
}

export { renderExpr, renderStmt };
